import { isEmpty } from "lodash";
import {
	type GetStaticPaths,
	type GetStaticProps,
	type GetStaticPropsResult,
	type NextPage,
} from "next";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useEffectOnce } from "react-use";
import SuperJSON from "superjson";
import { useAnalytics } from "use-analytics";
import { createActor, waitFor } from "xstate";

import type { DocumentMetadata } from "@moment/api-collab/api-types";

import {
	LOCAL_BASE_URL_DEFAULT,
	getIframeBaseUrl,
	getParentFrameOriginBaseUrl,
	isDesktopAppMode,
} from "~/auth/interop/common";
import { usePageEditorState } from "~/components/canvas/easel/usePageEditorState";
import { Page } from "~/components/page/Page";
import { SsrPage } from "~/components/page/SsrPage";
import { withAppShell } from "~/components/withAppShell";
import { appMachine } from "~/state/machines/app/app.machine";
import { type MessageType, isRunningInIframe, sendPostMessage } from "~/utils/iframe";

export type PageProps = {
	workspaceName: string;
	documentId: string;
	pageId: string;
	pageSlug: string;
	documentMetadata?: string;
	node?: string;
};

const generateUrlPath = (workspaceName: string, documentId: string, pageSlug: string) => {
	return `/d/${workspaceName}/${documentId}/${pageSlug}`;
};

/**
 * A wrapper that fetches the canvas data on the client instead of via SSR.
 */
export const PageWrapper: NextPage<PageProps> = (props) => {
	const { page } = useAnalytics();
	const router = useRouter();

	const { editorState } = usePageEditorState(props.documentId, props.pageId);
	let displayUrlPrefix: string | undefined;
	if (props.documentMetadata) {
		const metadata: DocumentMetadata = SuperJSON.parse(props.documentMetadata);
		displayUrlPrefix = metadata.publishSettings?.urlPrefix;
	}

	/*
					**** CONTEXT AROUND IFRAMES ****
		Updated - 05-14-2025

		iframe loading is spread across a bunch of places,
		but the heart of it is in this file.

		I am not sure how much we are updating this,
		so leaving a detailed comment on why iframes are implemented like this today.

		We load the SSR page first while the iframe is being loaded. 
		The page is rendered momentarily until the iframe is ready to give a progressive UI experience.

		The logic around onIframeReadyMessageHandler is to notify the the parent frame that the child iframe is loaded.

		When setting the iframe URL, we pass along the parent-origin so the child iframe can communicate back to the parent frame.
		We need to do this because in production, a document hostname can be coming from docs.moment.dev or www.moment.dev (our marketing website).
		Thus, we need to know which host the client is coming from so that the iframe can communicate back with the correct origin.

		On initial load, we register an event listener and wait for a message from the iframe that is it is ready.
		That message is sent in the BigRichTextEditor component when the iframe is ready to be displayed.
		See https://github.com/moment-eng/moment/blob/79ba48394d17c5d281ca4c8e021c383b9b6ab574/app/src/components/canvas/editor/BigRichTextEditor.tsx#L190-L197

		Once the iframe is loaded on the client side, which uses absolute positioning,
		the sibling div is hidden first and then removed by changing its display property once it is hidden.

					**** HOW DO YOU TEST IFRAMES ****
		I am not 100% sure how to test this locally.
		To simulate www.moment.dev/blog, I recommend taking over the staging preview build on vercel
		and setting docs-staging.moment.dev and iframes-staging.moment.dev to point to your branch's preview build.
		You then should be able to use www.moment-staging.dev/blog to simulate the production marketing environment..
	*/

	// The following state variables are for controlling the
	// iframe rendering state.
	const iframeBaseUrl = getIframeBaseUrl(process.env.APP_ENV);
	const [embedDocInIframe, setEmbedDocInIframe] = useState(false);
	const [iframeLoaded, setIframeLoaded] = useState(false);
	// Flag used to set the SsrPage component's display
	// property to "none", so it can be removed from the DOM.
	const [removeSsrPage, setRemoveSsrPage] = useState(false);
	// Track the URL that the iframe was initialized with.
	// We should not change the URL once the iframe has loaded
	// while the user is still viewing the same document since
	// it'll trigger an unnecessary full-page navigation.
	const [initializedIframeUrl, setInitializedIframeUrl] = useState<string | undefined>();

	// this is to be used when we are trying to redirect
	// to a new link from the iframe
	const redirectPathWithoutParentOrigin = () => {
		if (typeof window === "undefined") {
			throw new Error("cannot call redirectPathWithoutParentOrigin on the server");
		}

		if (!isRunningInIframe()) {
			throw new Error("cannot call redirectPathWithoutParentOrigin outside of iframe");
		}

		const url = new URL(window.location.href);

		const newParams = url.searchParams.toString() ? "?" + url.searchParams.toString() : "";

		const path = url.pathname;

		return `${path}${newParams}`;
	};

	const onIframeRouterPathChange = (event: MessageEvent) => {
		if (isRunningInIframe()) {
			window.removeEventListener("message", onIframeRouterPathChange);
			return;
		}

		const data = event.data;
		if (!data) {
			return;
		}

		const messageType: MessageType = event.data.type;
		if (messageType !== "routerPathChange") {
			return;
		}

		const args = data.args;
		if (!args || !args["path"] || !args["title"]) {
			console.error(`args is required for ${data.type} request from iframe.`);
			return;
		}

		// Skip updating the path name for /index to avoid
		// a redundant navigation history item.
		if (window.location.pathname === args["path"].replace("/index", "")) {
			return;
		}

		const realPath: string = args["path"];
		if (!realPath.startsWith("/")) {
			return;
		}

		let displayUrl = displayUrlPrefix;
		if (displayUrl) {
			const pathParts = realPath.split("/");
			if (pathParts.length >= 5 && pathParts[4] !== "index") {
				displayUrl += "/" + pathParts.slice(4).join("/");
			}
		}

		// DO NOT use Next.js router.replace here. It'll cause a
		// page navigation event when a URL mask is involved.
		window.history.replaceState({}, "", displayUrl || realPath);
		document.title = args["title"];
	};

	const onIframeReadyMessageHandler = async (event: MessageEvent) => {
		if (isRunningInIframe()) {
			window.removeEventListener("message", onIframeReadyMessageHandler);
			return;
		}

		if (event.origin !== iframeBaseUrl) {
			return;
		}

		const messageType: MessageType = event.data.type;
		if (messageType !== "iframeReady" || iframeLoaded) {
			return;
		}

		window.removeEventListener("message", onIframeReadyMessageHandler);
		const iframeUrl = new URL(
			generateUrlPath(props.workspaceName, props.documentId, props.pageSlug),
			iframeBaseUrl
		);

		setIframeLoaded(true);
		setInitializedIframeUrl(iframeUrl.toString());
		// Set the flag to remove the SsrPage asap (0 timeout).
		setTimeout(() => setRemoveSsrPage(true), 0);
	};

	// Analytics
	useEffectOnce(() => {
		page();
	});

	useEffectOnce(() => {
		if (isDesktopAppMode()) {
			return;
		}

		// Prevent the iframe URL from being used as a top-level URL
		// to load documents.
		if (
			!iframeBaseUrl.startsWith(LOCAL_BASE_URL_DEFAULT) &&
			window.location.href.startsWith(iframeBaseUrl) &&
			!isRunningInIframe()
		) {
			const redirectPath = redirectPathWithoutParentOrigin();
			const newRef = `${getParentFrameOriginBaseUrl(process.env.APP_ENV)}${redirectPath}`;
			document.writeln("Redirecting. Please wait...");
			window.location.href = newRef;
			return;
		}

		window.addEventListener("message", onIframeRouterPathChange);
		setEmbedDocInIframe(!isRunningInIframe());
	});

	useEffectOnce(() => {
		// Only listen for the iframe ready event in the parent frame.
		if (isRunningInIframe()) {
			return;
		}

		// console.log("page - adding iframe ready message handler", {
		// 	iframeBaseUrl,
		// 	parentOrigin,
		// 	currentIframeUrl,
		// });

		window.addEventListener("message", onIframeReadyMessageHandler);
	});

	useEffect(() => {
		if (!isRunningInIframe()) {
			return;
		}

		const path = redirectPathWithoutParentOrigin();

		// When the router path in the iframe changes,
		// let the parent know.
		sendPostMessage("routerPathChange", {
			path,
			title: document.title,
		});
	}, [router.asPath]);

	if (!isDesktopAppMode() && !editorState && !embedDocInIframe) {
		const defaultIframeUrl = new URL(
			generateUrlPath(props.workspaceName, props.documentId, props.pageSlug),
			iframeBaseUrl
		);

		return (
			<>
				<SsrPage {...props} />
				{/* Preload the iframe with hidden visibility. */}
				<iframe
					src={defaultIframeUrl.toString()}
					title="Document"
					style={{
						contentVisibility: "hidden",
					}}
				/>
			</>
		);
	}

	// When embedding a document in an iframe,
	// render the SsrPage alongside it. It ensures
	// that there isn't a hard subtree removal from
	// the DOM when switching to the iframe.
	if (embedDocInIframe) {
		const defaultIframeUrl = new URL(
			generateUrlPath(props.workspaceName, props.documentId, props.pageSlug),
			iframeBaseUrl
		);

		return (
			// Keep the SsrPage rendered while the iframe is loading.
			<>
				<div
					style={{
						contentVisibility: !iframeLoaded ? "visible" : "hidden",
						display: removeSsrPage ? "none" : "block",
					}}
				>
					<SsrPage {...props} />
				</div>
				<iframe
					src={initializedIframeUrl ?? defaultIframeUrl.toString()}
					title="Document"
					style={{
						width: "100%",
						height: "100%",
						position: "absolute",
						contentVisibility: iframeLoaded ? "visible" : "hidden",
						transition: "all 0.5s ease-in",
					}}
				/>
			</>
		);
	}

	return <Page />;
};

const notFound = (
	error: string,
	errorContext: Record<string, unknown> = {}
): GetStaticPropsResult<PageProps> => {
	console.error("logger hit an error, this is very bad.", {
		error,
		errorContext,
	});
	return {
		notFound: true,
		revalidate: 1,
	};
};

export const getStaticProps:
	| undefined
	| GetStaticProps<
			PageProps,
			{ workspaceName: string; documentId: string; pageSlug: string | string[] }
	  > = isDesktopAppMode()
	? undefined // Don't run this stuff in dev (desktop app doesn't have it in builds anyway)
	: async (context) => {
			try {
				if (!context.params) {
					return notFound("No params found");
				}

				const { workspaceName, documentId } = context.params;
				let pageSlug = Array.isArray(context.params.pageSlug)
					? context.params.pageSlug.join("/")
					: context.params.pageSlug;

				if (!isEmpty(pageSlug)) {
					pageSlug = pageSlug.split("?")[0] ?? ""; // Remove any query params
				}

				if (isEmpty(workspaceName) || isEmpty(documentId)) {
					return notFound("No workspaceName or documentId found", context.params);
				}

				const appActor = createActor(appMachine, {
					systemId: "app",
					input: {
						documents: {
							[documentId]: {
								id: documentId,
								title: documentId,
								createdAt: new Date(0),
								updatedAt: new Date(0),
								workspaces: [
									{
										id: `/${workspaceName}`,
										name: workspaceName,
										host: "",
										slug: workspaceName,
										owner: false,
									},
								],
							},
						},
					},
				});

				const documentActor = appActor.getSnapshot().context.documentActorRefs[documentId];

				if (!documentActor) {
					return notFound("No documentActor found", {
						documentId,
						appActor: appActor.getPersistedSnapshot(),
					});
				}

				documentActor.start();
				documentActor.send({ type: "document.opened" });

				await waitFor(documentActor, (state) => state.matches("viewing"), {
					// If the documentActor doesn't resolve in 5 seconds, timeout
					timeout: 5_000,
				});

				let pageId: string | undefined;
				const pagesMetadata =
					documentActor.getSnapshot().context.metadata?.pagesMetadata ?? [];
				if (isEmpty(pageSlug)) {
					// If we're looking for the index, find it by the slug
					pageId =
						pagesMetadata.find((page) => page.slug === "index")?.id ??
						// or if it's not found by the slug "index", grab the very first page
						pagesMetadata[0]?.id;
				} else {
					// Find the exact match if we're not looking for index
					pageId = pagesMetadata.find((page) => page.slug === pageSlug)?.id;
				}

				if (!pageId) {
					return notFound("Unable to find page id", {
						pageSlug,
						documentActor: documentActor.getPersistedSnapshot(),
					});
				}

				const pageActor = documentActor.getSnapshot().context.pageActorRefs[pageId];
				if (!pageActor) {
					return notFound("unable to find page actor", {
						pageId,
						documentActorContext: documentActor.getSnapshot().context,
					});
				}

				pageActor.start();
				pageActor.send({ type: "page.opened" });

				try {
					await waitFor(pageActor, (state) => state.matches({ data: "editing" }), {
						// If the pageActor doesn't resolve in 5 seconds, timeout
						timeout: 5_000,
					});
				} catch (error) {
					return notFound("Error waiting for pageActor", {
						error,
						context,
					});
				}

				const props: PageProps = {
					workspaceName,
					documentId,
					pageId,
					pageSlug: isEmpty(pageSlug) ? "index" : pageSlug,
					documentMetadata: SuperJSON.stringify(
						documentActor.getSnapshot().context.metadata
					),
					node: SuperJSON.stringify(
						pageActor.getSnapshot().context.editorState?.doc.toJSON()
					),
				};

				return {
					props,
					revalidate: 60,
				};
			} catch (error) {
				return notFound("Error fetching page", {
					error,
					context,
				});
			}
		};

export const getStaticPaths: undefined | GetStaticPaths = isDesktopAppMode()
	? undefined
	: async () => {
			return {
				// Don't generate any paths at build time.
				paths: [],

				// This will block rendering until the document is fetched from the server.
				fallback: "blocking",
			};
		};

export default withAppShell(PageWrapper);
